import React, { useState, useCallback } from 'react';
import type { StepUpMethod } from '@/types/auth';

interface StepUpModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Available step-up methods */
  methods: StepUpMethod[];
  /** Called when a method is selected and submitted */
  onSubmit: (method: StepUpMethod, payload: string) => void;
  /** Called when modal is dismissed */
  onClose: () => void;
  /** Loading state */
  isLoading?: boolean;
}

const methodConfig: Record<StepUpMethod, { icon: string; title: string; description: string }> = {
  retype: {
    icon: '\u2328',
    title: 'Redigitar texto',
    description: 'Digite novamente o texto de verificacao para confirmar sua identidade.',
  },
  email_otp: {
    icon: '\u2709',
    title: 'Codigo por e-mail',
    description: 'Receba um codigo de verificacao no seu e-mail cadastrado.',
  },
  totp: {
    icon: '\🔒',
    title: 'App autenticador',
    description: 'Use o codigo do seu app autenticador (Google Authenticator, Authy, etc).',
  },
};

/**
 * StepUpModal — Modal for MFA step-up authentication with 3 method options.
 */
export default function StepUpModal({
  isOpen,
  methods,
  onSubmit,
  onClose,
  isLoading = false,
}: StepUpModalProps) {
  const [selectedMethod, setSelectedMethod] = useState<StepUpMethod | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  const handleMethodSelect = useCallback((method: StepUpMethod) => {
    setSelectedMethod(method);
    setInputValue('');
    setError('');
  }, []);

  const handleSubmit = useCallback(() => {
    if (!selectedMethod) return;

    if (selectedMethod !== 'retype' && !inputValue.trim()) {
      setError('Por favor, insira o codigo de verificacao.');
      return;
    }

    onSubmit(selectedMethod, inputValue);
  }, [selectedMethod, inputValue, onSubmit]);

  const handleBack = useCallback(() => {
    setSelectedMethod(null);
    setInputValue('');
    setError('');
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md mx-4 bg-bg-secondary border border-bg-surface rounded-card shadow-2xl animate-slide-up">
        {/* Header */}
        <div className="p-6 border-b border-bg-surface">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-txt-primary font-sans">
              Verificacao Adicional
            </h2>
            <button
              onClick={onClose}
              className="text-txt-muted hover:text-txt-primary transition-colors text-xl leading-none"
            >
              &times;
            </button>
          </div>
          <p className="text-sm text-txt-muted mt-1 font-sans">
            Sua assinatura de digitacao ficou na zona cinza. Escolha um metodo de verificacao.
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {!selectedMethod ? (
            /* Method selection */
            <div className="space-y-3">
              {methods.map((method) => {
                const config = methodConfig[method];
                return (
                  <button
                    key={method}
                    onClick={() => handleMethodSelect(method)}
                    className="w-full flex items-center gap-4 p-4 rounded-card border border-bg-surface
                      hover:border-accent-brand/50 hover:bg-bg-surface/30 transition-all duration-200
                      text-left group"
                  >
                    <span className="text-2xl">{config.icon}</span>
                    <div>
                      <span className="block text-sm font-medium text-txt-primary group-hover:text-accent-brand transition-colors">
                        {config.title}
                      </span>
                      <span className="block text-xs text-txt-muted mt-0.5">
                        {config.description}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            /* Input step */
            <div className="space-y-4">
              <button
                onClick={handleBack}
                className="text-sm text-accent-brand hover:text-accent-brand/80 transition-colors font-sans"
              >
                &larr; Voltar
              </button>

              <div className="text-center py-2">
                <span className="text-3xl">{methodConfig[selectedMethod].icon}</span>
                <h3 className="text-base font-medium text-txt-primary mt-2 font-sans">
                  {methodConfig[selectedMethod].title}
                </h3>
                <p className="text-sm text-txt-muted mt-1 font-sans">
                  {methodConfig[selectedMethod].description}
                </p>
              </div>

              {selectedMethod !== 'retype' && (
                <div>
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                      setError('');
                    }}
                    placeholder={
                      selectedMethod === 'email_otp'
                        ? 'Digite o codigo de 6 digitos'
                        : 'Digite o codigo TOTP'
                    }
                    maxLength={6}
                    className="w-full px-4 py-3 bg-bg-primary border border-bg-surface rounded-lg
                      text-center text-2xl font-mono tracking-[0.5em] text-txt-primary
                      focus:outline-none focus:border-accent-brand transition-colors"
                    autoFocus
                  />
                  {error && (
                    <p className="text-xs text-accent-danger mt-2 font-sans">{error}</p>
                  )}
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="w-full py-3 bg-accent-brand hover:bg-accent-brand/90 text-white
                  rounded-lg font-medium text-sm font-sans transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Verificando...' : 'Verificar'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
